import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { X, Send, User, Mail, Phone, BadgeCheck, FileText, Download, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import ItineraryBuilder from './ItineraryBuilder';
import DocumentMessage from '../components/DocumentMessage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const AdminInquiryChat = ({ inquiryId, adminAuth, onClose, onSent }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itineraries, setItineraries] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [body, setBody] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const [sending, setSending] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/admin/inquiries/${inquiryId}/documents`, {
        method: 'POST', headers: { Authorization: `Basic ${adminAuth}` }, body: fd,
      });
      if (res.ok) { toast.success('Document sent'); await fetchThread(); if (onSent) onSent(); }
      else { const er = await res.json().catch(() => ({})); toast.error(er.detail || 'Upload failed'); }
    } catch { toast.error('Upload failed'); }
    finally { setUploadingDoc(false); e.target.value = ''; }
  };

  const fetchThread = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/inquiries/${inquiryId}/messages`, {
        headers: { Authorization: `Basic ${adminAuth}` },
      });
      if (res.ok) setData(await res.json());
    } catch {
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [inquiryId, adminAuth]);

  useEffect(() => {
    fetchThread();
    fetch(`${API_URL}/api/itineraries`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setItineraries)
      .catch(() => setItineraries([]));
  }, [fetchThread]);

  const send = async () => {
    if (!body.trim() && !templateId) {
      toast.error('Type a message or select an itinerary template');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/inquiries/${inquiryId}/messages`, {
        method: 'POST',
        headers: { Authorization: `Basic ${adminAuth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: body, itinerary_id: templateId || null }),
      });
      if (res.ok) {
        toast.success('Itinerary / message sent to client');
        setBody('');
        setTemplateId('');
        setEditorKey((k) => k + 1); // reset rich text editor
        await fetchThread();
        if (onSent) onSent();
      } else {
        toast.error('Failed to send');
      }
    } catch {
      toast.error('Connection error');
    } finally {
      setSending(false);
    }
  };

  const inquiry = data?.inquiry;
  const linked = data?.linked_account;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="admin-chat-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Conversation</h2>
            {inquiry && (
              <p className="text-sm text-gray-500">
                {inquiry.name} · {inquiry.destination || 'General inquiry'}
              </p>
            )}
            <div className="mt-2">
              {linked ? (
                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full" data-testid="linked-account-badge">
                  <BadgeCheck className="w-3 h-3" /> Registered account
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  <User className="w-3 h-3" /> Guest inquiry (no account)
                </span>
              )}
            </div>
            {inquiry && (
              <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {inquiry.email}</span>
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {inquiry.phone}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded" data-testid="close-chat-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="admin-conversation-thread">
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : (
            data?.messages?.map((m) => (
              <div key={m.id} className={`flex ${m.sender_type === 'team' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.sender_type === 'team' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                  <p className={`text-xs mb-1 ${m.sender_type === 'team' ? 'text-amber-100' : 'text-gray-500'}`}>
                    {m.sender_type === 'team' ? `Team (${m.sender_name})` : m.sender_name} · {fmt(m.created_at)}
                  </p>
                  {m.kind === 'itinerary' && m.pdf_url ? (
                    <div>
                      {m.text && <p className="text-sm mb-2 whitespace-pre-line">{m.text}</p>}
                      <a
                        href={`${API_URL}${m.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="itinerary-pdf-link"
                        className="inline-flex items-center gap-2 bg-white text-amber-700 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-amber-50"
                      >
                        <FileText className="w-4 h-4" /> {m.pdf_title || 'Itinerary'} (PDF) <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ) : m.kind === 'document' && m.attachment_url ? (
                    <DocumentMessage message={m} dark={m.sender_type === 'team'} />
                  ) : m.sender_type === 'team' ? (
                    <div className="blog-content text-sm" dangerouslySetInnerHTML={{ __html: m.text }} />
                  ) : (
                    <p className="text-sm whitespace-pre-line">{m.text}</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Composer */}
        <div className="border-t p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <label className="text-sm font-medium text-gray-700 shrink-0">Attach itinerary template:</label>
            <select
              data-testid="itinerary-template-select"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white flex-1"
            >
              <option value="">None (free-form only)</option>
              {itineraries.map((it) => (
                <option key={it.id} value={it.id}>{it.name} ({it.duration})</option>
              ))}
            </select>
          </div>
          <RichTextEditor key={editorKey} value="" onChange={setBody} adminAuth={adminAuth} />
          <p className="text-xs text-gray-500">
            Type the exact itinerary/message and/or attach a pre-built template — both are combined and sent to the client.
          </p>
          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowBuilder(true)} data-testid="open-itinerary-builder-btn">
                <FileText className="w-4 h-4 mr-2" /> Build Itinerary PDF
              </Button>
              <label className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-sm cursor-pointer hover:bg-gray-50" data-testid="admin-attach-doc">
                <Paperclip className="w-4 h-4 mr-2" />{uploadingDoc ? 'Uploading…' : 'Attach Document'}
                <input type="file" className="hidden" onChange={uploadDoc} accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.doc,.docx,.xls,.xlsx" />
              </label>
            </div>
            <Button onClick={send} disabled={sending} data-testid="admin-send-itinerary-btn" className="bg-amber-600 hover:bg-amber-700">
              <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send to Client'}
            </Button>
          </div>
        </div>
      </div>

      {showBuilder && (
        <ItineraryBuilder
          inquiryId={inquiryId}
          inquiry={inquiry}
          adminAuth={adminAuth}
          onClose={() => setShowBuilder(false)}
          onSent={() => { fetchThread(); if (onSent) onSent(); }}
        />
      )}
    </div>
  );
};

export default AdminInquiryChat;
